import { ONBOARDING_COPY, ONBOARDING_STEP_LABELS, type OnboardingStepKey } from "@/features/legal/config/onboarding.config";
import { cn } from "@/shared/utils/style.util";

type OnboardingProgressProps = {
  steps: OnboardingStepKey[]
  currentStep: OnboardingStepKey
};

type StepStatus = "complete" | "current" | "upcoming";

const SEGMENT_CLASS: Record<StepStatus, string> = {
  complete: "bg-primary",
  current: "bg-primary",
  upcoming: "bg-gray-200 dark:bg-gray-700",
};

const resolveStepStatus = (index: number, currentIndex: number): StepStatus => {
  if (index < currentIndex) return "complete";
  return index === currentIndex ? "current" : "upcoming";
};

// One segment per step, the login included: the caption above carries the
// visible wording, the labels stay for assistive technologies.
export const OnboardingProgress = ({ steps, currentStep }: OnboardingProgressProps) => {
  const labelledSteps = [
    { key: "login", label: ONBOARDING_STEP_LABELS.login, status: "complete" as StepStatus },
    ...steps.map((step, index) => ({
      key: step,
      label: ONBOARDING_STEP_LABELS[step],
      status: resolveStepStatus(index, steps.indexOf(currentStep)),
    })),
  ];

  return (
    <nav aria-label={ONBOARDING_COPY.PROGRESS_LABEL}>
      <ol className="flex gap-1.5">
        {labelledSteps.map(({ key, label, status }) => (
          <li key={key} className="flex-1" aria-current={status === "current" ? "step" : undefined}>
            <span aria-hidden className={cn("block h-1 rounded-full transition-colors duration-200", SEGMENT_CLASS[status])} />
            <span className="sr-only">{label}</span>
          </li>
        ))}
      </ol>
    </nav>
  );
};

OnboardingProgress.displayName = "OnboardingProgress";
